// The React chrome shell + router (design D1, D11a, DD1; UI.md §3): all four
// screens (Lobby, Colony, Match, Summary) plus one scaffold-only route (`/play`)
// proving the minimal in-match canvas view wires connection → store → LOD →
// renderer end-to-end (client-app "Minimal functional in-match canvas view"). Lobby
// (client-scaffold), Colony (colony-screen, ROADMAP 3.7), and now Match/Summary
// (match-screen, ROADMAP 3.8, task group 8) are all functional — no stubs remain on
// the four UI.md §3 routes (client-app "Four-route shell with future screens
// stubbed", MODIFIED).
//
// **This table is exactly those four routes again** (`rounds-refresh-restore`). `/review`,
// `/standings` and `/practice` are rounds-only paths and now live in the rounds-owned sub-router
// (`rounds/RoundsRoutes.tsx`); a live deployment never constructs it, so their absence in live mode
// is structural rather than conditional. That matters because the alternative was already showing:
// the standings route had to carry a sniff at which mode's seams were supplied in order to decide
// whether to mount, and two further rounds-only paths would have meant two more of them. The mode
// fork this shell *does* keep is the one it has always used — an opaque element substituted at a
// **shared** path (`homeScreen` at `/`, `sandboxScreen` at `/colony`): its presence IS the fork, and
// the shell learns nothing about what it mounted.
//
// `AppRoutes` takes no router of its own so tests can wrap it in a `MemoryRouter` at
// whatever `initialEntries` the scenario needs and drive it with hand-built seams
// (`App.test.tsx`). `App` is the production entry point (used by `main.tsx`,
// session-handoff task 7.1): `BrowserRouter → SessionController → AppRoutes` (design
// DD1) — the composition root is the single owner of the in-match session and
// derives every route seam itself, so `App` now takes a `SessionControllerDeps`
// bundle rather than the route seams directly.

import type { ReactElement } from 'react';
import { BrowserRouter, Route, Routes, useNavigate } from 'react-router-dom';
import type { InMatchCanvasDeps, InMatchRenderer } from './InMatchCanvas';
import { InMatchCanvas } from './InMatchCanvas';
import { Colony, type ColonyUnderlaySeams } from '../ui/screens/Colony';
import type { ColonyController } from '../ui/colony/controller';
import type { ChatSeam } from '../ui/colony/chat-seam';
import { Lobby } from '../ui/screens/Lobby';
import { Match, type MatchCameraLodSeam, type MatchHudSeams, type MatchLodSeam } from '../ui/screens/Match';
import { Summary } from '../ui/screens/Summary';
import { BenchmarkRoute } from './benchmark/BenchmarkRoute';
import { BENCHMARK_ROUTE } from './benchmark/BenchmarkScreen';
import {
  SessionController,
  type SessionControllerDeps,
  type SessionLobbyController,
  type SessionPhase,
} from './SessionController';
import { AudioRouteObserver, useAppAudioController } from '../audio/init-audio';
import { MatchOutcomeBanner } from '../ui/components/hud/MatchOutcomeBanner';
import type { SoundControlSeams } from '../ui/components/hud/SoundControl';

export interface AppRoutesProps {
  /** Optional pvp prompt overlays for the player match and presenter overview routes. */
  matchOverlay?: ReactElement;
  /** The Match screen's two-colony HUD data seams (`match-hud`): the live bearer its statistics
   * and prompt reads carry. Undefined outside a live pvp session, which leaves the panels without
   * a series rather than crashing. */
  matchHud?: MatchHudSeams;
  overviewOverlay?: ReactElement;
  summaryPrimaryAction?: { label: string; path: string; external?: boolean };
  /**
   * Deps for the scaffold-only `/play` route. Undefined in production until a real
   * match/session hand-off exists (later ROADMAP work); tests supply the mock
   * transport + fake scheduler/RNG/renderer here to drive the integration test
   * (task 5.3) without a GPU or a live server.
   */
  inMatchDeps?: InMatchCanvasDeps;
  /**
   * The `/colony` route's mutation-dispatch seam (design D3; colony-screen tasks.md
   * 9.2). Like `inMatchDeps`, this is the shell's injection point, not something
   * `Colony.tsx` builds for itself (client-app "Four-route shell with future
   * screens stubbed": "[the Colony screen] uses the shared session-scoped
   * connection ... it constructs no ... connection of its own"). Production leaves
   * it undefined until the same real match/session hand-off `inMatchDeps` is
   * waiting on exists (later ROADMAP work, matching the client-scaffold
   * precedent) — `Colony`'s own inert default controller (always `not-ready`)
   * covers that pre-connect reality. Tests inject `createFakeColonyController()`
   * alongside a fixture-seeded store (`App.test.tsx` task 9.1).
   */
  colonyController?: ColonyController;
  /**
   * The `/colony` route's live close-tier field-underlay seam bundle (design DD5/DD8,
   * `colony-field-canvas`): the session key, renderer factory, hive `Camera`, and the
   * viewport-carrying Colony LOD seam the composition root derives from the one session.
   * Undefined until a live session with a present `ownHive` exists, leaving the Colony
   * screen chrome-only over a background placeholder (never a crash), exactly as the
   * other route seams do.
   */
  colonyUnderlay?: ColonyUnderlaySeams;
  /**
   * The `/match` route's Wide-tier LOD request seam (design DD4; session-handoff
   * task 2.2). The composition root derives it from the single session's
   * `LodController`; until a session is live it is undefined and `Match` falls back
   * to its inert default (requesting nothing).
   */
  lod?: MatchLodSeam;
  /**
   * The `/match` route's interactive camera pan/zoom LOD seam (camera-pan-zoom,
   * `client-app` "Composition root injects the Match-route camera pan/zoom seam"). The
   * composition root derives it from the SAME session `LodController` as `lod` above (no
   * adapter needed — `LodController` already implements `{setZoom, setViewport}`).
   * Undefined until a session is live, matching the `lod` seam's own pre-wiring
   * convention (`Match`'s inert default drives nothing).
   */
  cameraLod?: MatchCameraLodSeam;
  /**
   * The `/match` and `/play` routes' world-renderer factory (design DD3/DD4). The
   * composition root supplies the real PixiJS `CanvasRenderer` factory — a superset
   * renderer that satisfies both the Match and in-match canvas renderer shapes;
   * undefined until a session is live, leaving each canvas a background placeholder.
   */
  createRenderer?: (container: HTMLElement, tickRateHz: number) => InMatchRenderer;
  /**
   * The `/` (Lobby) route's hand-off seam (design DD5; session-handoff task 3.6). The
   * composition root owns the one `LobbyClient` + roster poll loop and injects this
   * seam so `Lobby.tsx` stays presentational — it constructs no client/connection of
   * its own. Undefined in a bare `AppRoutes` test mount, where `Lobby` falls back to
   * its inert default (renders create/join over no hand-off).
   */
  lobbyController?: SessionLobbyController;
  /**
   * The in-match `SoundControl` seam bundle (client-audio DD1/DD6), threaded from the app-root
   * `initAudio` mount through `SessionController` into the Colony + Match HUDs. Undefined in a
   * bare `AppRoutes` test mount (and on the first render before the audio controller arms),
   * where each screen falls back to rendering no control (additive-only, matching the
   * `INERT_CONTROLLER`/`INERT_LOD` "renders nothing until wired" precedent).
   */
  sound?: SoundControlSeams;
  /**
   * The composition root's live session phase (onboarding design D-Gate), threaded to
   * the `/colony` route so the first-run guidance overlay shows only while
   * `sessionPhase === 'in-match'`. Undefined in a bare `AppRoutes` test mount, where the
   * Colony guide simply never shows (matching a not-in-match session).
   */
  colonySessionPhase?: SessionPhase;
  /**
   * The `/colony` route's **pvp prototype** Beekeeper seam (`docs/dev/pvp-prototype.md`). Its
   * presence is the fork, in the mould of every other optional seam here: undefined unless the
   * server advertises pvp, and in every bare mount, where `Colony` renders exactly as it always
   * has. Deliberately NOT the `colonyChat` seam `sandbox-screen-consolidation` removed — that one
   * was rounds mode reaching into the live screen; this one is a live-mode prototype, wired only
   * by the live composition root, and the rounds root never supplies it.
   */
  colonyPvpChat?: ChatSeam;
  /**
   * The rounds composition root's replacement for the live Colony screen at `/colony`
   * (`sandbox-screen-consolidation` task 6.4; `client-colony` "In rounds mode this screen is not
   * mounted"). Its **presence IS the mode fork**, in the mould of every other optional seam here:
   * undefined under `GameMode::Live` and in a bare mount, where `/colony` mounts `ColonyRoute`
   * exactly as before. The three rounds-only Colony seams this replaces (`colonyRun`,
   * `colonyPromptCooldown`, `colonyChat`) are gone with the Colony rounds path itself — the sandbox
   * screen owns them now, and the shell no longer carries a rounds prop the live screen must ignore.
   *
   * It is an element rather than a seam bundle deliberately. `AppRoutes` is shared by both
   * composition roots, so anything it had to *know* about the sandbox screen — its prop surface,
   * its coordinator, its transports — would put a rounds-shaped dependency on the live path, which
   * is the conditional `RoundsApp`'s header exists to reject. The shell substitutes an opaque
   * element at one route and learns nothing else; the rounds root, which already owns every rounds
   * seam, builds it.
   *
   * Scoped to `/colony` and nowhere else. `/review` in particular stays mounted (**D7** and
   * `client-rounds-review`, MODIFIED): the graded subject still needs the route even though the
   * sandbox path no longer offers any way to reach it.
   */
  sandboxScreen?: ReactElement;
  /**
   * The rounds composition root's replacement for the live Lobby at `/` (`home-screen` **D1**;
   * `client-app` "the `/` route is mode-dependent"). Its **presence IS the mode fork**, exactly as
   * `sandboxScreen`'s is: undefined under `GameMode::Live` and in a bare mount, where `/` mounts
   * the Lobby precisely as before.
   *
   * **A substitution, not a sixth route.** Two counts are easy to conflate and must not be: UI.md
   * §3's *rounds-mode screen inventory* stays at four, because Home replaces the Lobby there
   * rather than adding to it, while this router still exposes *five routes* across both modes.
   * Neither moves.
   *
   * An element rather than a seam bundle, for the reason `sandboxScreen` is one: `AppRoutes` is
   * shared by both composition roots, so anything it had to *know* about Home — its entry points,
   * its credential restore, its ambient asset — would put a rounds-shaped dependency on the live
   * path. The shell substitutes an opaque element at one route and learns nothing else.
   */
  homeScreen?: ReactElement;
}

function InMatchRoute({ deps }: { deps?: InMatchCanvasDeps }) {
  if (deps === undefined) {
    return (
      <div data-testid="in-match-canvas-placeholder">
        Not connected — join a match from the Lobby.
      </div>
    );
  }
  return <InMatchCanvas {...deps} />;
}

/** The `/match` route's shell wiring (task 8.2; design D-match-6, DD4). Supplies the
 * navigation seam over the router's own `useNavigate` (so crossing the LOD threshold
 * into Close hands off to `/colony`) plus — now that the composition root exists —
 * the Wide-tier LOD request seam, the interactive camera pan/zoom LOD seam
 * (camera-pan-zoom), and the world-renderer factory derived from the one session
 * (session-handoff task 2.2). All stay optional so a pre-session mount (no live seams
 * yet) falls back to `Match`'s own inert defaults/placeholder canvas. */
function MatchRoute({
  lod,
  cameraLod,
  createRenderer,
  sound,
  overlay,
  hud,
}: {
  lod?: MatchLodSeam;
  cameraLod?: MatchCameraLodSeam;
  createRenderer?: (container: HTMLElement, tickRateHz: number) => InMatchRenderer;
  sound?: SoundControlSeams;
  overlay?: ReactElement;
  hud?: MatchHudSeams;
}) {
  const navigate = useNavigate();
  return <><Match onNavigate={navigate} lod={lod} cameraLod={cameraLod} createRenderer={createRenderer} sound={sound} hud={hud} />{overlay}</>;
}

/** The `/colony` route's shell wiring (camera-pan-zoom): supplies the navigation seam
 * over the router's own `useNavigate` so zooming out below the LOD threshold hands off
 * to `/match` (UI.md §3's bidirectional zoom-gesture nav, the reciprocal of `MatchRoute`'s
 * own zoom-in-to-`/colony` crossing) — previously the `/colony` route rendered `<Colony>`
 * inline with no navigation seam at all, since nothing needed one before this change. */
function ColonyRoute({
  controller,
  underlay,
  sound,
  sessionPhase,
  pvpChat,
}: {
  controller?: ColonyController;
  underlay?: ColonyUnderlaySeams;
  sound?: SoundControlSeams;
  sessionPhase?: SessionPhase;
  pvpChat?: ChatSeam;
}) {
  const navigate = useNavigate();
  return (
    <Colony
      controller={controller}
      underlay={underlay}
      sound={sound}
      sessionPhase={sessionPhase}
      onNavigate={navigate}
      pvpChat={pvpChat}
    />
  );
}

/** The `/summary` route's shell wiring (task 8.2): the CTAs' navigation seam over
 * the router's own `useNavigate` — `Summary` reads the store only and drives no
 * connection, so there is no deferred seam left to flag here (contrast
 * `MatchRoute`'s LOD note). */
function SummaryRoute({ primaryAction }: { primaryAction?: { label: string; path: string; external?: boolean } }) {
  const navigate = useNavigate();
  return <Summary onNavigate={navigate} primaryAction={primaryAction} />;
}

export function AppRoutes({
  inMatchDeps,
  colonyController,
  colonyUnderlay,
  lod,
  cameraLod,
  createRenderer,
  lobbyController,
  sound,
  summaryPrimaryAction,
  colonySessionPhase,
  colonyPvpChat,
  sandboxScreen,
  homeScreen,
  matchOverlay,
  matchHud,
  overviewOverlay,
}: AppRoutesProps = {}) {
  // **The match-outcome banner** (`player-chat-and-outcome` **D-5**) — a layer beside the router,
  // not a route's child. The terminal `MatchResult` arrives while the player is on Colony or Match
  // and `SessionController` navigates straight to `/summary`, so a banner owned by any one screen
  // would flash for a frame; mounted here it survives that navigation and reports the outcome
  // wherever the player lands.
  //
  // **Live mode only**, and by the shell's own existing fork rather than a new conditional: the
  // presence of `homeScreen`/`sandboxScreen` IS the rounds fork (see their prop docs), and rounds
  // mode deliberately says nothing of the kind — a graded round produces a placement among N and a
  // tournament mean, which the review screen already communicates properly. The exclusion is belt
  // and braces on top of a structural one: `matchResult` is only ever set by the live wire binder,
  // which a rounds deployment never runs.
  const roundsRoot = homeScreen !== undefined || sandboxScreen !== undefined;

  return (
    <>
      {!roundsRoot && <MatchOutcomeBanner />}
    <Routes>
      {/* Mode-dependent (`home-screen` **D1**): Home under `GameMode::Rounds`, the Lobby under
          `GameMode::Live`. A rounds deployment mounts no lobby routes at all, so the Lobby has
          nothing here to bind to — and the Lobby deliberately grows no rounds branch, since a
          single screen whose content depended on the mounted mode is exactly the
          provenance-dependent component UI.md §3 argues against. */}
      <Route path="/" element={homeScreen ?? <Lobby controller={lobbyController} />} />
      <Route
        path="/colony"
        element={
          sandboxScreen ?? (
            <ColonyRoute
              controller={colonyController}
              underlay={colonyUnderlay}
              sound={sound}
              sessionPhase={colonySessionPhase}
              pvpChat={colonyPvpChat}
            />
          )
        }
      />
      <Route
        path="/match"
        element={<MatchRoute lod={lod} cameraLod={cameraLod} createRenderer={createRenderer} sound={sound} overlay={matchOverlay} hud={matchHud} />}
      />
      <Route
        path="/overview"
        element={<MatchRoute lod={lod} cameraLod={cameraLod} createRenderer={createRenderer} sound={sound} overlay={overviewOverlay} hud={matchHud} />}
      />
      <Route path="/summary" element={<SummaryRoute primaryAction={summaryPrimaryAction} />} />
      {/* **No rounds-only path is declared here** (`client-app`, MODIFIED by
          `rounds-refresh-restore`). `/review/:round`, `/standings` and `/practice` live in the
          rounds-owned sub-router (`rounds/RoundsRoutes.tsx`), which a live deployment never
          constructs — so there is no rounds path to suppress and, crucially, no conditional here that
          decides a route by inspecting which mode's seams were supplied. The standings route used to
          carry exactly such a sniff; it is deleted rather than duplicated for two more paths. */}
      {/* `/benchmark` (`device-benchmark`): the developer measurement instrument, and the one
          path here that belongs to *neither* mode. It is not a screen in either mode's inventory,
          so it re-opens nothing in UI.md §3 and neither mode's screen count moves — and because it
          is mode-independent it belongs in this shared table rather than the rounds sub-router,
          matching the server, which mounts the endpoint behind it at the single mount-time fork.
          Mounted as an OPAQUE element taking no seam from this shell at all: it constructs no
          session, needs no credential and reads no store slice, so the shell learns nothing about
          it and removing it later would touch no other route. Nothing navigates here; it is
          reached only by typing the path. */}
      <Route path={BENCHMARK_ROUTE} element={<BenchmarkRoute />} />
      <Route path="/play" element={<InMatchRoute deps={inMatchDeps} />} />
      {/* In rounds mode this table is one of **two** sibling routers over one location, and the
          rounds-only paths belong to the other — so matching nothing here is the expected outcome
          rather than a miss. Without an explicit catch-all, React Router logs "No routes matched
          location /standings" on every such navigation, which is a warning about the arrangement
          itself. Under `GameMode::Live` nothing reaches it: every live path is declared above. */}
      <Route path="*" element={null} />
    </Routes>
    </>
  );
}

/** The production entry point (`main.tsx`, task 7.1; design DD1): `BrowserRouter →
 * SessionController → AppRoutes`, now with the app-root audio mount alongside.
 * `SessionController` is a router child (it calls `useNavigate()` directly) and is the
 * sole owner of the in-match session for the app's whole lifetime — it derives and
 * injects every `AppRoutesProps` seam itself, so `App` takes the composition root's
 * injectable deps bundle, not route seams.
 *
 * The client-audio layer (DD1/DD10) is installed here as a sibling to `SessionController`:
 * `App` owns one `initAudio` controller for the app's lifetime, mounts a thin
 * `AudioRouteObserver` (a `useLocation` sibling that feeds the driver screen-exit / re-entry
 * signals — screen identity is router-owned, not store-derived, review-2 F2), and threads the
 * controller's `sound` seam into `SessionController` so the in-match Colony/Match HUDs render
 * the `SoundControl` wired to the shared lifecycle + engine. The controller is created in an
 * effect and disposed in its cleanup, so a React unmount / HMR replace tears the engine down
 * (spec R8-unmount) and a StrictMode double-invoke creates a fresh, un-leaked controller. */
export function App(props: SessionControllerDeps) {
  // The mount/dispose lifecycle moved into `useAppAudioController` verbatim so the rounds-mode
  // root installs the same layer (implementation-review F1); this tree is unchanged.
  const audio = useAppAudioController();
  return (
    <BrowserRouter>
      {audio !== null && <AudioRouteObserver controller={audio} />}
      <SessionController {...props} sound={audio?.sound} />
    </BrowserRouter>
  );
}
